import { useState } from "react";
import { t } from "i18next";
import moment from "moment";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router";

import { AVAILIO_LOGO, FAIRGATE_LOGO } from "@constants/assets";
import { dprTerms, legalTerms } from "@constants/urls";
import { routeEnum, supportEnum } from "@enums/route.enum";
import { DateFormats } from "@enums/shared.enum";
import organisationSlice from "@slices/organisation.slice";

const CookiePolicy: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const toggle = (index: number) => {
    setOpenIndex(prev => (prev === index ? null : index));
  };
  const dispatch = useDispatch();
  const resetLocationUnitStore = () => {
    dispatch(organisationSlice.actions.changeLocationId({ id: "0" }));
    dispatch(organisationSlice.actions.changeResourceId({ id: "0" }));
  };
  return (
    <>
      <header>
        <div className=" fg-header-main" data-testid={"header-test"}>
          <div className="container">
            {pathname !== routeEnum.Home &&
              !pathname.includes(supportEnum.Auth) && (
                <button
                  className="btn fg-btn fg-btn-back"
                  onClick={() => {
                    navigate(-1);
                    resetLocationUnitStore();
                  }}
                  data-testid="headerBackButton"
                >
                  {t("back")}
                </button>
              )}

            <div className="fg-header-block" data-testid="header-block">
              <Link
                to="/"
                onClick={resetLocationUnitStore}
                data-testid="fairgate-image"
              >
                <img
                  src={AVAILIO_LOGO}
                  className="fg-logo-img"
                  alt="Availio logo"
                />
              </Link>
            </div>
          </div>
        </div>
      </header>
      <div className="container fg-main-wrapper">
        <div className="row">
          <div className="col-sm-12">
            <div className="fg-cookie-policy-wrapper">
              <h4>
                Cookie Policy
                <br />
                <i>Letzte Aktualisierung: 05.01.2026</i>
              </h4>

              <p>
                Diese Cookie-Richtlinie erklärt, wie Cookies und ähnliche
                Technologien verwendet werden, wenn Sie unsere Availio Web-App
                besuchen.
              </p>

              <div className="accordion fg-cookie-accordion my-3">
                {/* SECTION 1 */}
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button
                      type="button"
                      className={`accordion-button ${
                        openIndex === 0 ? "" : "collapsed"
                      }`}
                      aria-expanded={openIndex === 0}
                      onClick={() => toggle(0)}
                    >
                      1. Was sind Cookies?
                    </button>
                  </h2>

                  <div
                    className={`accordion-collapse ${
                      openIndex === 0 ? "is-open" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      <p>
                        Cookies sind kleine Textdateien, die auf Ihrem Gerät
                        gespeichert werden, wenn Sie eine Website besuchen.
                      </p>
                    </div>
                  </div>
                </div>

                {/* SECTION 2 */}
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button
                      type="button"
                      className={`accordion-button ${
                        openIndex === 1 ? "" : "collapsed"
                      }`}
                      aria-expanded={openIndex === 1}
                      onClick={() => toggle(1)}
                    >
                      2. Welche Arten von Cookies verwenden wir?
                    </button>
                  </h2>

                  <div
                    className={`accordion-collapse ${
                      openIndex === 1 ? "is-open" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      <p>
                        Wir verwenden Cookies und ähnliche Technologien, um die
                        Nutzererfahrung zu verbessern und bestimmte Funktionen
                        bereitzustellen.
                      </p>

                      <h6>Notwendige Cookies</h6>
                      <p>
                        Diese Cookies sind erforderlich, damit unsere Website
                        ordnungsgemäß funktioniert.
                      </p>

                      <h6>Analyse- und Anpassungs-Cookies</h6>
                      <p>
                        Diese Cookies sammeln anonyme Informationen zur
                        Verbesserung der Website.
                      </p>

                      <div className="table-responsive">
                        <table className="table table-bordered table-sm fg-cookie-list-table">
                          <thead>
                            <tr>
                              <th>Cookie</th> <th>Used by</th>
                              <th>Purpose</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>_ga_*</td> <td>3rd Party (google.com)</td>
                              <td>Counts page views (Google Analytics).</td>
                            </tr>
                            <tr>
                              <td>_gid</td> <td>3rd Party (google.com)</td>
                              <td>Stores usage statistics.</td>
                            </tr>
                            <tr>
                              <td>_ga</td> <td>3rd Party (google.com)</td>
                              <td>Tracks sessions and campaigns.</td>
                            </tr>
                            <tr>
                              <td>_gat_gtag_UA_*</td>
                              <td>3rd Party (google.com)</td>
                              <td>Limits request rate.</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION 3 */}
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button
                      type="button"
                      className={`accordion-button ${
                        openIndex === 2 ? "" : "collapsed"
                      }`}
                      aria-expanded={openIndex === 2}
                      onClick={() => toggle(2)}
                    >
                      3. Einwilligung und Verwaltung von Cookies
                    </button>
                  </h2>

                  <div
                    className={`accordion-collapse ${
                      openIndex === 2 ? "is-open" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      <p>
                        Sie können Ihre Cookie-Einstellungen jederzeit über das
                        Cookie-Banner oder Ihren Browser ändern.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button
                      type="button"
                      className={`accordion-button ${
                        openIndex === 3 ? "" : "collapsed"
                      }`}
                      aria-expanded={openIndex === 3}
                      onClick={() => toggle(3)}
                    >
                      4. Speicherdauer und Drittanbieterdaten
                    </button>
                  </h2>
                  {openIndex === 3 && (
                    <div className="accordion-body">
                      <p>
                        <b>Speicherdauer:</b> Die Dauer, für die Cookies auf
                        Ihrem Gerät verbleiben, variiert. Sitzungscookies werden
                        gelöscht, sobald Sie Ihren Browser schließen, während
                        persistente Cookies länger auf Ihrem Gerät verbleiben.
                      </p>
                      <p>
                        <b>Datenübermittlung an Drittländer:</b> Wenn durch
                        Cookies gesammelte Daten in Drittländer außerhalb der EU
                        übermittelt werden, stellen wir sicher, dass angemessene
                        Schutzmaßnahmen getroffen werden, wie z.B. der Abschluss
                        von Standardvertragsklauseln.
                      </p>
                    </div>
                  )}
                </div>
                {/* SECTION 4 */}
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button
                      type="button"
                      className={`accordion-button ${
                        openIndex === 4 ? "" : "collapsed"
                      }`}
                      aria-expanded={openIndex === 4}
                      onClick={() => toggle(4)}
                    >
                      5. Ihre Rechte und Kontakt
                    </button>
                  </h2>

                  <div
                    className={`accordion-collapse ${
                      openIndex === 4 ? "is-open" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      <p>
                        <b>Widerruf der Einwilligung:</b> Sie haben jederzeit
                        das Recht, Ihre Einwilligung zur Verwendung von Cookies
                        zu widerrufen. Dies können Sie tun, indem Sie die
                        Cookie-Einstellungen auf unserer Website anpassen oder
                        Ihren Browser entsprechend konfigurieren.
                      </p>
                      <p>
                        <b>Kontakt:</b> Wenn Sie Fragen oder Bedenken bezüglich
                        unserer Cookie-Richtlinie haben, kontaktieren Sie
                        unseren Website-Anbieter bitte unter:
                        <a href="mailto:support@fairgate.ch">
                          <b>support@fairgate.ch</b>
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button
                      type="button"
                      className={`accordion-button ${
                        openIndex === 5 ? "" : "collapsed"
                      }`}
                      aria-expanded={openIndex === 5}
                      onClick={() => toggle(5)}
                    >
                      6. Änderungen an dieser Cookie-Richtlinie
                    </button>
                  </h2>

                  <div
                    className={`accordion-collapse ${
                      openIndex === 5 ? "is-open" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      <p>
                        Wir behalten uns das Recht vor, diese Cookie-Richtlinie
                        jederzeit zu ändern, um sie an gesetzliche Anforderungen
                        oder Änderungen unserer Cookie-Nutzung anzupassen. Bitte
                        überprüfen Sie diese Seite regelmäßig, um über
                        Aktualisierungen informiert zu bleiben.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="footer fg-footer-block" data-testid={"footer-test"}>
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="d-flex align-items-center flex-column position-relative">
                <img
                  src={FAIRGATE_LOGO}
                  className="float-md-end fg-footer-logo text-center d-flex d-md-block mx-auto mt-2"
                  alt="Fairgate logo"
                  data-testid="footer-logo"
                />
                <p className="mt-2">
                  {t("footerContent", {
                    year: moment().format(DateFormats.Y),
                  })}
                </p>
                <ul className="navbar-nav mb-2 flex-row align-items-center justify-content-center justify-content-md-start">
                  <li className="nav-item">
                    <a
                      className="nav-link border-end px-2 py-1"
                      href={dprTerms}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("fairgatessGDPRTerms")}
                    </a>
                  </li>
                  <li className="nav-item">
                    <a
                      className="nav-link px-2 py-1"
                      href={legalTerms}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("fairgatessLegalTerms")}
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookiePolicy;
